import {createAction, createAsyncThunk, createReducer} from '@reduxjs/toolkit';

import {ENDPOINTS} from 'constants/endpoints';
import {CurrentAddressType} from 'desktop/types/addressType';
import {CustomerAddressesType} from 'desktop/types/customerAddressesType';
import {apiConfig, HTTP_REQUEST_HEADER, HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE} from 'libs/api/api';

export const initialState: CustomerAddressesType = {
    addresses: [],
    billing_address_id: null,
    isLoaded: false,
    isLoading: false,
    shipping_address_id: null,
};

export const getCustomerAddresses = createAsyncThunk<{data: CustomerAddressesType}>(
    'CHECKOUT@GET_CUSTOMER_ADDRESSES',
    () =>
        apiConfig.instanceGusaBackend({
            // for IE11 browser, b/c he doesn't update addresses when he should
            headers: {[HTTP_REQUEST_HEADER.PRAGMA]: 'no-cache'},
            method: 'get',
            url: `${ENDPOINTS.GET_ADDRESSES}`,
        }),
);

export const getCustomerAddressesFromOrder = createAsyncThunk<void, string>(
    'CHECKOUT@GET_CUSTOMER_ADDRESSES_FROM_ORDER',
    token =>
        apiConfig.instanceGusaBackend({
            method: 'get',
            url: `${ENDPOINTS.GET_ADDRESSES_FROM_ORDER({token})}`,
        }),
);

export const saveCustomerAddresses = createAsyncThunk<
    void,
    object,
    {rejectValue: {data: {message: string}}}
>('CHECKOUT@SAVE_CUSTOMER_ADDRESSES', async (data, {rejectWithValue}) => {
    try {
        return await apiConfig.instanceGusaBackend({
            data,
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
            },
            method: 'post',
            url: `${ENDPOINTS.SAVE_ADDRESS}`,
        });
    } catch (e) {
        return rejectWithValue(e);
    }
});

export const getReviewData = createAsyncThunk<{data: {address: CustomerAddressesType}}>(
    'CHECKOUT@GET_REVIEW_DATA',
    () =>
        apiConfig.instanceGusaBackend({
            method: 'get',
            url: ENDPOINTS.PAYMENT_GET_REVIEW_DATA,
        }),
);

export const saveEditedCustomerAddresses = createAsyncThunk<void, {id: string; data: object}>(
    'CHECKOUT@SAVE_EDITED_CUSTOMER_ADDRESSES',
    ({data, id}) =>
        apiConfig.instanceGusaBackend({
            baseURL: apiConfig.API_URL,
            data,
            method: 'put',
            url: `${ENDPOINTS.CUSTOMER_ADDRESS}/${id}`,
        }),
);

export const addNewCustomerAddresses = createAsyncThunk<void, object>(
    'CHECKOUT@ADD_NEW_CUSTOMER_ADDRESSES',
    data =>
        apiConfig.instanceGusaBackend({
            baseURL: apiConfig.API_URL,
            data,
            method: 'post',
            url: `${ENDPOINTS.CUSTOMER_ADDRESS}`,
        }),
);

export const startAddNewCustomerAddressSaga = createAction<string>(
    'CHECKOUT@START_ADD_NEW_CUSTOMER_ADDRESS_SAGA',
);

export const startSavingCustomerAddressSaga = createAction<string>(
    'CHECKOUT@START_SAVING_CUSTOMER_ADDRESS_SAGA',
);

export const startUpdateCustomerAddressSaga = createAction<{
    id: string;
    type: CurrentAddressType | null;
}>('CHECKOUT@START_UPDATE_CUSTOMER_ADDRESS_SAGA');

export const restoreAddressesAfterCanceledEditing = createAction(
    'CHECKOUT@RESTORE_ADDRESSES_AFTER_CANCELED_EDITING',
);

export const setAddressLoaded = createAction<boolean>('CHECKOUT@SET_ADDRESS_LOADED');

/**
 * Customer addresses reducer
 *
 * @return {CustomerAddressesType} reducer of customer addresses
 */
export const customerAddresses = createReducer(initialState, builder =>
    builder
        .addCase(getCustomerAddresses.fulfilled, (state, action) => ({
            ...state,
            ...action.payload.data,
        }))
        .addCase(getReviewData.fulfilled, (state, action) => ({
            ...state,
            ...action.payload.data.address,
        }))
        .addCase(saveEditedCustomerAddresses.fulfilled, state => ({
            ...state,
            isLoading: false,
        }))
        .addCase(saveEditedCustomerAddresses.pending, state => ({
            ...state,
            isLoading: true,
        }))
        .addCase(setAddressLoaded, (state, action) => ({
            ...state,
            isLoaded: action.payload,
        })),
);
